import { Card, CardContent, MenuItem, TextField } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { ColorDynamic, Inline, useResponsiveValue } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { TextBox } from '@superdispatch/ui-lab';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  DEFAULT_LOADS_PAGE_SIZE,
  isLoadsPageSize,
  LoadsPageSize,
  LOADS_PAGE_SIZES,
} from '../data/LoadsPageParams';
import { NAVIGATION_BOTTOM_BAR_HEIGHT_CSS_VAR } from 'shared/layout/NavigationHeaderBannerHeight';
import { useFlag } from 'shared/settings/FeatureToggles';

// Reserves the fixed bar's height so it doesn't cover the last load
const PaginationPlaceholder = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
`;

// Keeps pagination reachable on long pages (e.g. 100 rows per page)
const FixedContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(${NAVIGATION_BOTTOM_BAR_HEIGHT_CSS_VAR}, 0px);
  z-index: 10;

  ${({ theme }) => theme.breakpoints.up('md')} {
    left: 88px;
  }
`;

const StyledCard = styled(Card)`
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-bottom: none;
`;

const LegacyMobileCard = styled(Card)`
  margin-right: -17px;
  margin-left: -17px;
  .MuiCardContent-root {
    padding-left: 0;
    padding-right: 0;
  }
`;

const StyledPagination = styled(Pagination)<{ $isCompact?: boolean }>`
  .MuiPaginationItem-root {
    color: ${ColorDynamic.Blue300};
    ${({ $isCompact }) => ($isCompact ? 'margin: 0 1px;' : '')}
  }
  .MuiPaginationItem-page.Mui-disabled {
    color: ${ColorDynamic.Dark100};
  }
  .Mui-selected {
    background-color: ${ColorDynamic.Blue50};
  }
  .MuiPagination-ul {
    flex-wrap: nowrap;
  }
`;

interface LoadsPagePaginationProps {
  count: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: LoadsPageSize) => void;
}

export function LoadsPagePagination({
  count,
  currentPage,
  pageSize = DEFAULT_LOADS_PAGE_SIZE,
  onPageChange,
  onPageSizeChange,
}: LoadsPagePaginationProps) {
  const isPaginationEnabled = useFlag('carrier_loads_pagination');
  const maxPage = Math.ceil(count / pageSize);
  const isMobile = useResponsiveValue(true, false, false);
  // Fits the select and the pages in one line on mobile
  const isCompact = isMobile && onPageSizeChange != null;

  const pagination = (
    <StyledPagination
      $isCompact={isCompact}
      page={currentPage}
      siblingCount={
        isCompact || (maxPage - currentPage < 4 && maxPage > 99) ? 0 : undefined
      }
      size={isCompact ? 'medium' : 'large'}
      count={maxPage}
      onChange={(_, page) => {
        onPageChange(page);
      }}
    />
  );

  const renderCardContent = (
    <CardContent>
      {onPageSizeChange ? (
        <Inline
          space={isCompact ? 'xsmall' : 'large'}
          verticalAlign="center"
          noWrap={isCompact}
        >
          <Inline space="xsmall" verticalAlign="center" noWrap={true}>
            {!isMobile && <TextBox color="secondary">Rows per page</TextBox>}
            <TextField
              select={true}
              size="small"
              variant="outlined"
              value={pageSize}
              inputProps={{ 'aria-label': 'Rows per page' }}
              onChange={(event) => {
                const nextPageSize = Number(event.target.value);

                if (isLoadsPageSize(nextPageSize)) {
                  onPageSizeChange(nextPageSize);
                }
              }}
            >
              {LOADS_PAGE_SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
          </Inline>
          {pagination}
        </Inline>
      ) : (
        pagination
      )}
    </CardContent>
  );

  if (!isPaginationEnabled) {
    return (
      <Box marginTop="xsmall">
        {isMobile ? (
          <LegacyMobileCard
            role="navigation"
            aria-label="Pagination Navigation"
          >
            {renderCardContent}
          </LegacyMobileCard>
        ) : (
          <Card>{renderCardContent}</Card>
        )}
      </Box>
    );
  }

  return (
    <FixedPagination isMobile={isMobile}>{renderCardContent}</FixedPagination>
  );
}

interface FixedPaginationProps {
  isMobile: boolean;
  children: ReactNode;
}

function FixedPagination({ isMobile, children }: FixedPaginationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current!;

    function updateHeight() {
      setHeight(element.offsetHeight);
    }

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <PaginationPlaceholder $height={height}>
      <FixedContainer ref={containerRef}>
        <StyledCard
          role={isMobile ? 'navigation' : undefined}
          aria-label={isMobile ? 'Pagination Navigation' : undefined}
        >
          {children}
        </StyledCard>
      </FixedContainer>
    </PaginationPlaceholder>
  );
}
